import React, {
  createContext,
  useContext,
  useRef,
  useCallback,
  useMemo,
} from 'react';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  isRevalidating: boolean;
  version: number;
}

interface DataCacheContextType {
  get: <T>(key: string) => CacheEntry<T> | null;
  set: <T>(key: string, data: T) => void;
  invalidate: (key: string) => void;
  invalidateAll: () => void;
  invalidatePattern: (pattern: string | RegExp) => void;
  invalidateByTable: (table: string) => void;
  isFresh: (key: string, ttlSeconds?: number) => boolean;
  setRevalidating: (key: string, isRevalidating: boolean) => void;
  getCacheStats: () => { size: number; keys: string[] };
}

const DataCacheContext = createContext<DataCacheContextType | undefined>(undefined);

export function DataCacheProvider({ children }: { children: React.ReactNode }) {
  const cacheRef = useRef<Map<string, CacheEntry<unknown>>>(new Map());

  const get = useCallback(<T,>(key: string): CacheEntry<T> | null => {
    const entry = cacheRef.current.get(key);
    return entry ? (entry as CacheEntry<T>) : null;
  }, []);

  const set = useCallback(<T,>(key: string, data: T) => {
    const existing = cacheRef.current.get(key);
    const version = existing ? existing.version + 1 : 1;

    cacheRef.current.set(key, {
      data,
      timestamp: Date.now(),
      isRevalidating: false,
      version,
    });
  }, []);

  const invalidate = useCallback((key: string) => {
    cacheRef.current.delete(key);
  }, []);

  const invalidateAll = useCallback(() => {
    cacheRef.current.clear();
  }, []);

  const invalidatePattern = useCallback((pattern: string | RegExp) => {
    const regex = typeof pattern === 'string' ? new RegExp(pattern) : pattern;

    for (const key of cacheRef.current.keys()) {
      if (regex.test(key)) {
        cacheRef.current.delete(key);
      }
    }
  }, []);

  const invalidateByTable = useCallback((table: string) => {
    for (const key of cacheRef.current.keys()) {
      if (
        key === table ||
        key.startsWith(`${table}:`) ||
        key.startsWith(`${table}-`) ||
        key.includes(`:${table}:`)
      ) {
        cacheRef.current.delete(key);
      }
    }
  }, []);

  const isFresh = useCallback((key: string, ttlSeconds = 30): boolean => {
    const entry = cacheRef.current.get(key);
    if (!entry) return false;

    return Date.now() - entry.timestamp < ttlSeconds * 1000;
  }, []);

  const setRevalidating = useCallback((key: string, isRevalidating: boolean) => {
    const entry = cacheRef.current.get(key);
    if (!entry) return;

    cacheRef.current.set(key, {
      ...entry,
      isRevalidating,
    });
  }, []);

  const getCacheStats = useCallback(() => {
    return {
      size: cacheRef.current.size,
      keys: Array.from(cacheRef.current.keys()),
    };
  }, []);

  const value = useMemo<DataCacheContextType>(
    () => ({
      get,
      set,
      invalidate,
      invalidateAll,
      invalidatePattern,
      invalidateByTable,
      isFresh,
      setRevalidating,
      getCacheStats,
    }),
    [
      get,
      set,
      invalidate,
      invalidateAll,
      invalidatePattern,
      invalidateByTable,
      isFresh,
      setRevalidating,
      getCacheStats,
    ]
  );

  return (
    <DataCacheContext.Provider value={value}>
      {children}
    </DataCacheContext.Provider>
  );
}

export function useDataCache(): DataCacheContextType {
  const context = useContext(DataCacheContext);

  if (!context) {
    throw new Error('useDataCache must be used within DataCacheProvider');
  }

  return context;
}